import Flyout from "@/components/Flyout";
import { Slot, useGlobalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import { View, ActivityIndicator } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useTheme } from "@/hooks/useTheme";

// Lazy load DrawerBody so its module (including React Navigation) fully unloads on unmount
const DrawerBody = React.lazy(() => import("@/components/DrawerBody"));

function toBool(v?: string | string[]) {
  return Array.isArray(v) ? v.length > 0 && v.some(Boolean) : typeof v === "string" && !!v;
}

export const unstable_settings = {
  applications: {
    initialRouteName: "applications",
  },
  chat: {
    initialRouteName: "chat",
  },
  clients: {
    initialRouteName: "clients",
  },
  contacts: {
    initialRouteName: "contacts",
  },
  courses: {
    initialRouteName: "courses",
  },
  expenses: {
    initialRouteName: "expenses",
  },
  estimates: {
    initialRouteName: "estimates",
  },
  incidents: {
    initialRouteName: "incidents",
  },
  investigations: {
    initialRouteName: "investigations",
  },
  invoices: {
    initialRouteName: "invoices",
  },
  payroll: {
    initialRouteName: "payroll",
  },
  projects: {
    initialRouteName: "projects",
  },
  shifts: {
    initialRouteName: "shifts",
  },
  timecards: {
    initialRouteName: "time_cards",
  },
  users: {
    initialRouteName: "users",
  },
  schedules: {
    initialRouteName: "schedules",
  },
};

export default function TableEntityLayout() {
  const router = useRouter();
  const params = useGlobalSearchParams<{ panel?: string; id?: string; pinned?: string; new?: string }>();
  const { top } = useSafeAreaInsets();
  const { drawerPinned } = useNavigationGuard();
  const { colors } = useTheme();

  const isOpen = useMemo(() => toBool(params.panel), [params]);
  const [visible, setVisible] = useState(isOpen);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const hasPanel = typeof params.panel === "string" && !!params.panel && params.panel !== "undefined";

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }

    if (hasPanel && !drawerPinned) {
      setVisible(true);
    } else if (drawerPinned) {
      setVisible(false);
    } else {
      closeTimeoutRef.current = setTimeout(() => setVisible(false), 200);
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [params.panel, drawerPinned]);

  const close = () => {
    router.setParams({
      panel: undefined,
      id: undefined,
      tab: undefined,
      new: undefined,
      edit: undefined,
      related: undefined,
    });
  };

  return (
    <View style={{ flex: 1, paddingTop: top }}>
      <Slot screenOptions={{}} />
      {visible && (
        <Flyout
          style={{ flexGrow: 0, height: "100%", overflow: "hidden" }}
          visible={visible}
          setVisible={setVisible}
          onUnmount={close}
          storageKey="shared-flyout"
        >
          <Suspense
            fallback={
              <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            }
          >
            <DrawerBody />
          </Suspense>
        </Flyout>
      )}
    </View>
  );
}
